/**
 * Utility retrieves private and public keys out of mnemonic phrase
 *
 * <i>Copyright (c) 2019 Energi Cryptocurrency - https://energi.world</i><br>
 * Proprietary License
 *
 *
 * @module mnemonic-parser.js
 * @class MnemonicParser
 * @since 0.4.0
*/

// Note: the addressGapLimit = 20 -> https://github.com/bitcoin/bips/blob/master/bip-0044.mediawiki#user-content-address-gap-limit

const mnemonicToSeed = require('bip39').mnemonicToSeed,
    bip32 = require('bip32'),
    utils = require('itsa-utils'),
    energiNetworkBip32 = require('../constants/bip32-network').energi;

/**
 * Takes a mnemonic phrase and recoveres a count amount of private/public key set.
 *
 * @method getKeysFromMnemonic
 * @param mnemonic {String} a Mnenomic Phrase of 24 words, divided by whitespaces
 * @param [extraPhrase] {String} an optional extra (password) phrase
 * @param [purpose=44] {Number} first number of the BIP44 specification: likely to be 44
 * @param [coinType=204] {Number} second number of the BIP44 specification: coin type
 * @param [account=0] {Number} wallet id that belongs with the mnemonic
 * @param [external=true] {Boolean} whether the external keypairs should be generated. If not, then the internal chain (also known as change addresses) is used.
 * @param [skip=0] {Number} How many HD wallet keypairs to skip
 * @param [count=100] {Number} How many keypairs to return
 * @return {Array} of objects with the properties:
 * <ul>
 *      <li>privateKey {String}</li>
 *      <li>publicKey {String}</li>
 * </ul>
 * @since 0.4.0
 */
const getKeysFromMnemonic = async (mnemonic, extraPhrase, purpose=44, coinType=204, account=0, external=true, skip=0, count=100) => {
    let seedBytes, bip32Node, energiNode, keys, i;
    const internalOrExternal = external ? 0 : 1;

    seedBytes = await mnemonicToSeed(mnemonic, extraPhrase);
    bip32Node = bip32.fromSeed(seedBytes, energiNetworkBip32);
    energiNode = bip32Node.derivePath(`m/${purpose}'/${coinType}'/${account}'/${internalOrExternal}`);

    keys = [];

    for (i=skip; i<count+skip; i++) {
        if (i) {
            await utils.delay(1); // keep system responsive
        }
        keys.push(getKeys(energiNode, i));
    }

    return keys;
};

/**
 * Retrieves the "index's" private and public key out of an Energi node.
 *
 * @method getKeys
 * @param energiNode {Object} an BIP44 Energi node, derived with a specific path.
 * @param index {nukber} the nth keypait to be derived
 * @protected
 * @return {Object} with the properties:
 * <ul>
 *      <li>privateKey {String}</li>
 *      <li>publicKey {String}</li>
 * </ul>
 * @since 0.4.0
 */
const getKeys = (energiNode, index) => {
    let child, privateKey, publicKey;
    child = energiNode.derive(index);
    privateKey = child.toWIF();
    publicKey = child.publicKey.toString('hex');
    return {
        privateKey,
        publicKey
    };
};

module.exports = {
    getKeysFromMnemonic
};
